import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase/firebase';
import AppTheme from '../theme/AppTheme';
import ColorModeSelect from '../theme/ColorModeSelect';
import ProfileDropdown from '../components/ProfileDropdown';
import { jsPDF } from 'jspdf';

interface BillingRecord {
  id: string;
  uid: string;
  email: string;
  cargoData: {
    tons: number;
    from: string;
    to: string;
    transportMode: string;
    price: string;
    estimatedDelivery: string;
  };
  paymentMethod: string;
  billingAddress: string;
  paymentStatus: string;
  timestamp: any;
}

const BillingHistoryPage: React.FC = () => {
  const [user, setUser] = React.useState<any>(null);
  const [billingRecords, setBillingRecords] = React.useState<BillingRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedBill, setSelectedBill] = React.useState<BillingRecord | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);

  // Monitor user authentication
  React.useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return unsubscribeAuth;
  }, []);

  // Fetch user's billing history from Firestore
  React.useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    console.log(user.email);
    const q = query(
      collection(db, 'cargo_billings'),
      where('email', '==', user.email),
   
    );
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const bills: BillingRecord[] = [];
        querySnapshot.forEach((doc) => {
          bills.push({ id: doc.id, ...doc.data() } as BillingRecord);
        });
        setBillingRecords(bills);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching billing history: ', error);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [user]);

  const handleViewDetails = (bill: BillingRecord) => {
    setSelectedBill(bill);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBill(null);
  };

  const generateInvoice = (bill: BillingRecord) => {
    const doc = new jsPDF();
    doc.setFontSize(22);
    doc.text("Cargo Transport Invoice", 105, 20, { align: 'center' } as any);
    
    doc.setFontSize(12);
    doc.text(`Invoice Date: ${bill.timestamp?.toDate().toLocaleDateString() || new Date().toLocaleDateString()}`, 20, 40);
    doc.text(`Invoice ID: INV-${bill.id.substring(0, 6)}`, 20, 50);
    
    doc.text("Cargo Details:", 20, 70);
    doc.text(`Weight: ${bill.cargoData.tons} ton(s)`, 30, 80);
    doc.text(`From: ${bill.cargoData.from}`, 30, 90);
    doc.text(`To: ${bill.cargoData.to}`, 30, 100);
    doc.text(`Transport Mode: ${bill.cargoData.transportMode.toUpperCase()}`, 30, 110);
    doc.text(`Estimated Delivery: ${bill.cargoData.estimatedDelivery} day(s)`, 30, 120);
    
    doc.text("Payment Summary:", 20, 140);
    
    // Price calculation
    const basePrice = parseInt(bill.cargoData.price.replace(/[^\d]/g, ''));
    const tax = Math.round(basePrice * 0.1);
    const handling = Math.round(basePrice * 0.05);
    const total = basePrice + tax + handling;
    
    doc.text(`Base Price: $${basePrice}`, 30, 150);
    doc.text(`Tax (10%): $${tax}`, 30, 160);
    doc.text(`Handling Fee (5%): $${handling}`, 30, 170);
    doc.text(`Total Amount: $${total}`, 30, 185);
    
    doc.text("Thank you for choosing our Cargo Management System!", 105, 220, { align: 'center' } as any);
    
    doc.save(`invoice-${bill.id.substring(0, 6)}.pdf`);
  };

  // Component to display bill details
  const BillDetailsDisplay: React.FC<{ bill: BillingRecord }> = ({ bill }) => {
    // Price calculation
    const basePrice = parseInt(bill.cargoData.price.replace(/[^\d]/g, ''));
    const tax = Math.round(basePrice * 0.1);
    const handling = Math.round(basePrice * 0.05);
    const total = basePrice + tax + handling;

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Cargo Transport Invoice
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Shipping Details
            </Typography>
            <Typography variant="body2">
              {bill.cargoData.tons} ton(s) from <strong>{bill.cargoData.from}</strong> to <strong>{bill.cargoData.to}</strong>
            </Typography>
            <Typography variant="body2">
              Transport mode: <Chip label={bill.cargoData.transportMode.toUpperCase()} color="primary" size="small" />
            </Typography>
            <Typography variant="body2">
              Estimated delivery: {bill.cargoData.estimatedDelivery} day(s)
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Payment Details
            </Typography>
            <Typography variant="body2">
              Payment method: {bill.paymentMethod}
            </Typography>
            <Typography variant="body2">
              Status: <Chip label={bill.paymentStatus} color="success" size="small" />
            </Typography>
            <Typography variant="body2">
              Date: {bill.timestamp?.toDate().toLocaleString() || 'Unknown'}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Price Breakdown
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Base price:</Typography>
              <Typography variant="body2">${basePrice}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Tax (10%):</Typography>
              <Typography variant="body2">${tax}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Handling fee (5%):</Typography>
              <Typography variant="body2">${handling}</Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
              <Typography variant="subtitle1" fontWeight="bold">${total}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      
      <Box
        sx={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 2000,
          display: 'flex',
          gap: 1,
        }}
      >
        <ColorModeSelect />
        <ProfileDropdown
          userName={user?.displayName || 'User'}
          userEmail={user?.email || 'unknown@example.com'}
        />
      </Box>

      <Container sx={{ mt: 6 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Billing History
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : billingRecords.length === 0 ? (
          <Typography align="center" variant="body1" sx={{ mt: 2 }}>
            You don't have any billing records yet.
          </Typography>
        ) : (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {billingRecords.map((bill) => {
              const timeString = bill.timestamp?.toDate
                ? bill.timestamp.toDate().toLocaleString()
                : String(bill.timestamp);
              
              // Calculate total price
              const basePrice = parseInt(bill.cargoData.price.replace(/[^\d]/g, ''));
              const total = basePrice + Math.round(basePrice * 0.15); // 10% tax + 5% handling

              return (
                <Grid item xs={12} sm={6} md={4} key={bill.id}>
                  <Card
                    component={Paper}
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 2px 5px rgba(0,0,0,0.4)'
                          : '0 2px 5px rgba(0,0,0,0.1)',
                    }}
                  >
                    <CardHeader
                      titleTypographyProps={{ variant: 'h6' }}
                      title={`${bill.cargoData.tons} ton(s) cargo`}
                      subheader={`From ${bill.cargoData.from} to ${bill.cargoData.to}`}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment date: {timeString}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Transport mode: <strong>{bill.cargoData.transportMode.toUpperCase()}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Total amount: <strong>${total}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Status: <Chip label={bill.paymentStatus} color="success" size="small" sx={{ mt: 0.5 }} />
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => generateInvoice(bill)}
                      >
                        Download Invoice
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleViewDetails(bill)}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Dialog for showing detailed bill */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>
          Invoice Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBill ? (
            <BillDetailsDisplay bill={selectedBill} />
          ) : (
            <Typography>No billing information available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {selectedBill && (
            <Button 
              variant="contained" 
              onClick={() => generateInvoice(selectedBill)}
            >
              Download Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default BillingHistoryPage;